import { Button } from "@msuite/ui/button";
import { ChartHeader } from "@msuite/ui/charts";
import { DashboardTile } from "@msuite/ui/dashboard";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { BookingsReviewListItem } from "@msuite/utils/types";
import { useState } from "react";
import { useNavigate } from "react-router";
import { usePendingReviewBookings } from "@/hooks/use-pending-review-bookings";
import { AssignmentReferenceBookingsModal } from "@/modals/partners/assignment-reference-bookings-modal";
import type { DashboardTileProps } from "../shared";
import { DashboardPartnerBookingReviewItem } from "./dashboard-partner-booking-review-item";

/**
 * The host's queue of partner timesheets. Offered by the layout only to an organisation that
 * hosts a partner and to a caller with `read Partner`.
 */
export function DashboardPartnerBookingReviewsItem({ item }: DashboardTileProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const navigate = useNavigate();
	const bookingsModal = useDisclosure();

	/** State */
	const [selected, setSelected] = useState<BookingsReviewListItem | null>(null);

	/** Query */
	const { bookings, isLoading, queryKey, refetch } = usePendingReviewBookings();

	/** Functions */
	function handleOnOpen(booking: BookingsReviewListItem) {
		setSelected(booking);
		bookingsModal.onOpen();
	}

	async function handleOnComplete() {
		await refetch();
	}

	/** Render */
	return (
		<DashboardTile item={item}>
			<ChartHeader
				title={t("dashboard.partner_booking_reviews")}
				description={t("dashboard.n_partner_booking_reviews", { count: `${bookings.length}` })}
			/>
			<DashboardTile.List
				isLoading={isLoading}
				data={bookings}
				queryKey={queryKey}
			>
				{bookings.map((booking) => (
					<DashboardTile.List.Item
						key={booking.id}
						onClick={() => handleOnOpen(booking)}
						data-cy="dashboard-partner-booking-review"
					>
						<DashboardPartnerBookingReviewItem booking={booking} />
					</DashboardTile.List.Item>
				))}
			</DashboardTile.List>
			<div className="absolute -right-5 -bottom-3">
				<Button
					size="xs"
					variant="white"
					icon={<Icon name="ArrowRight" />}
					onClick={() => navigate("/partners?partners-tabs=2")}
					data-cy="dashboard-partner-booking-reviews-show-all"
				>
					{t("general.show_all")}
				</Button>
			</div>
			{bookingsModal.isOpen && selected && (
				<AssignmentReferenceBookingsModal
					isOpen={bookingsModal.isOpen}
					onClose={bookingsModal.onClose}
					referenceId={selected.id}
					partnershipId={selected.partnershipId}
					date={selected.date}
					onComplete={handleOnComplete}
				/>
			)}
		</DashboardTile>
	);
}
